import { inject, Injectable, NgZone, Signal } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';

export type AppPlatform = 'web' | 'android' | 'ios';

// Must match $uv-mobile in src/styles/_breakpoints.scss.
export const UV_MOBILE_QUERY: string = '(max-width: 767.98px)';

@Injectable({
  providedIn: 'root'
})
export class PlatformService {
  readonly platform: AppPlatform = PlatformService.resolvePlatform();
  readonly isNative: boolean = this.platform !== 'web';

  readonly isMobileViewport: Signal<boolean> = toSignal(
    inject(BreakpointObserver).observe(UV_MOBILE_QUERY).pipe(map(state => state.matches)),
    { initialValue: window.matchMedia(UV_MOBILE_QUERY).matches }
  );

  private readonly zone = inject(NgZone);
  private readonly appStateChanges = new Subject<boolean>();
  private foreground: boolean = true;

  // Emits whenever the native app changes foreground state, inside the Angular
  // zone (plugin callbacks arrive outside it). Never emits on web, where
  // document.visibilitychange already covers the same ground.
  readonly appState$: Observable<boolean> = this.appStateChanges.asObservable();

  // False while the native app is in the background, where the WebView keeps
  // running JS and SignalR. Always true on web.
  get isForeground(): boolean {
    return this.foreground;
  }

  constructor() {
    // iOS reports appStateChange inactive while the app is still on screen
    // under Control Center, the notification shade or a call banner; only
    // didEnterBackground (pause) means background there. Android's
    // appStateChange already follows onStop.
    if (this.platform === 'ios') {
      void App.addListener('pause', () => this.zone.run(() => this.setForeground(false)));
      void App.addListener('resume', () => this.zone.run(() => this.setForeground(true)));
    } else if (this.isNative) {
      void App.addListener('appStateChange', state => this.zone.run(() => this.setForeground(state.isActive)));
    }
    // The keyboard plugin hides the iOS form bar, and with it the only way to
    // dismiss the keyboard without tapping outside the field.
    if (this.platform === 'ios') {
      void Keyboard.setAccessoryBarVisible({ isVisible: true });
    }
  }

  private setForeground(foreground: boolean): void {
    this.foreground = foreground;
    this.appStateChanges.next(foreground);
  }

  // Needed where no injector exists yet: the module decides whether to persist
  // the auth slice to sessionStorage before the application is bootstrapped.
  static isNativePlatform(): boolean {
    return PlatformService.resolvePlatform() !== 'web';
  }

  private static resolvePlatform(): AppPlatform {
    const platform: string = Capacitor.getPlatform();
    return platform === 'android' || platform === 'ios' ? platform : 'web';
  }
}
